/**
 * DigoLauncher — Phase 1 (Docs/DIGO_VOICE_ADMIN_ASSISTANT_PLAN.md §7/§8).
 *
 * 2026-08-18: visual redesign, replacing the hologram/smoke avatar — direct
 * feedback was it read as a flat rotating photo, then (v2, a round
 * character) as a "potato," then (v3, an illustrated human bust) as
 * childish. This version uses a real, configurable photo instead of any
 * hand-drawn character — see frontend/public/digo-avatar/README.md for how
 * to swap the image (drop a file in, no code change). Falls back to a
 * generic silhouette if no image is present, never breaks.
 *
 * Interaction shape (kept from the version that WAS well received): a
 * floating mic button and a floating keyboard button beside the avatar,
 * not a persistent chat panel — the keyboard button reveals a single
 * inline input, and answers show as a speech bubble above the avatar that
 * fades on its own rather than staying open as a chat window.
 *
 * Backend wiring is unchanged from the original Phase 1 build: real POST
 * /digo/chat over SSE, same manual fetch+reader parsing pattern as
 * CoPilotPanel.tsx (EventSource can't send the Authorization header this
 * API needs). Mic uses real browser SpeechRecognition where available,
 * honest fallback to typing otherwise — not simulated.
 *
 * 2026-08-18 (later same day): the pulsing-ring "he's talking" stand-in is
 * now backed by a real rigged 3D avatar with actual lip-sync (DigoAvatar3D,
 * via @met4citizen/talkinghead) whenever WebGL/the model load succeeds —
 * the photo stays as the immediate visual (and the permanent fallback if
 * the 3D avatar fails to load).
 *
 * 2026-08-19: three more things, all real, none simulated:
 * (1) Real voice — digo_engine.py now synthesizes the reply through the
 *     same TTS entry point the calling pipeline uses and sends it as a
 *     SEPARATE "audio" SSE event, arriving after "done" (the underlying
 *     chat engine's own last event) since synthesis only starts once the
 *     full reply text is known — this is also why voice audibly lags
 *     behind the (already-streaming, token-by-token) text: it's a second,
 *     fully sequential network round-trip that only starts once the text
 *     is finished, same architectural gap already tracked for the calling
 *     pipeline in Docs/CALLING_RESPONSIVENESS_FIXES_PLAN.md's "Incremental
 *     TTS playback" item — nothing here streams TTS incrementally yet. If
 *     synthesis fails server-side (fails open — the text reply is
 *     unaffected), no "audio" event arrives and nothing plays for that
 *     turn, rather than faking it. playReplyAudio() below always plays it
 *     through this component's own AudioContext (see that function's own
 *     note for why — it used to hand off to the 3D avatar once ready,
 *     which turned out to fail silently on turn 2+).
 * (2) The photo avatar is now fully swapped out (unmounted) once the 3D
 *     avatar is ready, not layered underneath it — avoids a photo-circle
 *     showing through the 3D canvas's transparent background.
 * (3) The 3D canvas itself has no panel/box behind it (DigoAvatar3D.tsx
 *     sets the renderer's clear color to transparent) — just the figure.
 *
 * 2026-08-19 (later same day): mic is now continuous, not one-shot — it
 * stays listening across multiple turns until the user explicitly taps it
 * off (or closes Digo), auto-restarting itself when the browser's own
 * recognition session ends on a pause (see the SpeechRecognition effect
 * below for why that restart is needed even with continuous:true).
 */
import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../config/api';
import DigoAvatar3D, { DigoAvatar3DHandle } from './DigoAvatar3D';
import './digo.css';

type AvatarState = 'idle' | 'speaking' | 'listening';

// frontend/public/digo-avatar/avatar.png — swap that file to change the
// avatar. Not bundled/imported so replacing it needs no rebuild.
const AVATAR_SRC = '/digo-avatar/avatar.png';

const AvatarFallback: React.FC = () => (
  <div className="digo-avatar-fallback" aria-hidden="true">
    <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="4" fill="currentColor" opacity="0.55" />
      <path d="M4 20c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="currentColor" opacity="0.55" />
    </svg>
  </div>
);

const DigoLauncher: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [popping, setPopping] = useState(false);
  const [settled, setSettled] = useState(false);
  const [avatarState, setAvatarState] = useState<AvatarState>('idle');
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [kbOpen, setKbOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [bubble, setBubble] = useState<{ role: string; text: string } | null>(null);
  const [bubbleShow, setBubbleShow] = useState(false);
  // Full conversation log, shown as a persistent panel below the avatar —
  // separate from `bubble`, which stays as the transient above-the-avatar
  // callout it always was. `historyRef` (below) is a plain ref sent to the
  // backend each turn and doesn't trigger re-renders, so this is tracked
  // independently as state purely for display.
  const [transcript, setTranscript] = useState<{ role: 'user' | 'assistant'; text: string }[]>([]);
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const [imgFailed, setImgFailed] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [avatar3DReady, setAvatar3DReady] = useState(false);
  const [avatar3DFailed, setAvatar3DFailed] = useState(false);

  const historyRef = useRef<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const recognitionRef = useRef<any>(null);
  // Whether the user WANTS the mic on — separate from `listening` state
  // because recognition.onend fires and restarts asynchronously; this ref
  // is what onend checks to decide whether to auto-restart (see effect
  // below) or actually stop, and is read live regardless of which render's
  // closure holds the callback (same reasoning as avatar3DReadyRef).
  const micWantedRef = useRef(false);
  const bubbleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const avatar3DRef = useRef<DigoAvatar3DHandle>(null);
  // sendMessage is called from the mic's recognition.onresult handler, whose
  // closure is fixed at mount (see the useEffect below, [] deps) — a plain
  // avatar3DReady state read there would always see its mount-time value
  // (false), never the real one, so voice-triggered replies would silently
  // never get lip-sync. A ref sidesteps that: refs are read live regardless
  // of which render's closure is holding them.
  const avatar3DReadyRef = useRef(false);
  // Same closure-staleness reasoning as avatar3DReadyRef — sendMessage's
  // "audio" handler needs the reply text at the moment audio arrives, and
  // must work from the mic's fixed-at-mount closure too.
  const lastReplyTextRef = useRef('');
  // Playback path when the 3D avatar isn't ready/failed — separate context
  // from DigoAvatar3D's own (that one also has a silent-buffer fallback
  // path; this one only ever plays real audio).
  const playbackCtxRef = useRef<AudioContext | null>(null);
  // digo_engine.py now streams one "audio" SSE event PER SENTENCE instead
  // of one at the end of the whole reply (2026-08-19) — decoded chunks are
  // pushed here in arrival order (which the backend guarantees matches
  // sentence order, see digo_engine.py's reorder buffer) and drained
  // strictly one at a time via audioPlayingRef/playNextQueuedAudio below,
  // so back-to-back sentences never overlap/talk over each other.
  const audioQueueRef = useRef<{ buffer: AudioBuffer; text: string }[]>([]);
  const audioPlayingRef = useRef(false);
  // Mirrors `busy` state but read inside sendMessage's own guard instead of
  // the `busy` closure variable — sendMessage is invoked from the mic's
  // recognition.onresult, whose closure is fixed at mount (same staleness
  // as avatar3DReadyRef/lastReplyTextRef above), so a plain `busy` state
  // read there would always see its mount-time value (false) and never
  // actually block overlapping requests once the mic restarts itself
  // continuously.
  const busyRef = useRef(false);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ block: 'nearest' });
  }, [transcript]);

  useEffect(() => {
    const SpeechRecCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecCtor) {
      const recognition = new SpeechRecCtor();
      recognition.lang = 'en-US';
      // continuous:true so the mic stays open across multiple utterances
      // instead of shutting off after the first one — the whole point of
      // "stays open until I pause it." interimResults:true is needed for
      // continuous mode to keep detecting speech boundaries reliably in
      // Chrome; only isFinal results are ever acted on below (interim
      // results are the browser's own in-progress guess, not sent).
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      recognition.onresult = (e: any) => {
        // Only finalized results carry real recognized speech — the
        // engine's own voice-activity detection is what decides a result
        // exists at all, so silence/background noise never reaches here in
        // the first place. A finalized-but-empty transcript (can happen on
        // very short noises) is dropped rather than sent as a blank turn.
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const result = e.results[i];
          if (result.isFinal) {
            const text = result[0].transcript.trim();
            if (text) sendMessage(text);
          }
        }
      };
      recognition.onerror = (e: any) => {
        // 'no-speech' fires routinely in continuous mode during normal
        // silence between utterances — not a real error, the mic should
        // just keep listening (onend below handles the restart).
        if (e?.error === 'no-speech' || e?.error === 'aborted') return;
        micWantedRef.current = false;
        setListening(false);
        setAvatarState('idle');
        setHint("Couldn't access the mic here — try the keyboard instead.");
      };
      recognition.onend = () => {
        // Browsers end the recognition session on their own after a pause
        // in speech, even with continuous:true — restart automatically
        // unless the user actually turned the mic off via toggleMic, so
        // "stays open" means stays open rather than "re-arms for one more
        // utterance."
        if (micWantedRef.current) {
          try {
            recognition.start();
          } catch {
            // Already running (can happen if start() raced onend) — ignore.
          }
        } else {
          setListening(false);
        }
      };
      recognitionRef.current = recognition;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showBubble = (role: string, text: string, autoHideMs = 5200) => {
    setBubble({ role, text });
    setBubbleShow(true);
    setHint(null);
    if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
    if (autoHideMs > 0) {
      bubbleTimerRef.current = setTimeout(() => setBubbleShow(false), autoHideMs);
    }
  };

  const openDigo = () => {
    setOpen(true);
    setSettled(false);
    setPopping(true);
    // A fresh DigoAvatar3D instance mounts each open (see JSX below) — reset
    // readiness so the fade-in doesn't fire early using stale state from a
    // previous open. Deliberately not resetting avatar3DFailed: once the 3D
    // avatar has failed to load, stay on the photo permanently rather than
    // re-attempting a ~36MB load every time the panel opens.
    setAvatar3DReady(false);
    avatar3DReadyRef.current = false;
    setTimeout(() => {
      setPopping(false);
      setSettled(true);
      showBubble('Digo', 'Hi — tap the mic or the keyboard.');
    }, 440);
  };

  const closeDigo = () => {
    // Stop the mic on close — otherwise it would keep listening (and
    // auto-restarting per the onend handler) in the background with the
    // panel gone.
    if (micWantedRef.current) {
      micWantedRef.current = false;
      recognitionRef.current?.stop();
    }
    setListening(false);
    setOpen(false);
    setSettled(false);
    setPopping(false);
    setBubbleShow(false);
    setKbOpen(false);
    if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
  };

  const toggleMic = () => {
    if (!recognitionRef.current) {
      setHint("Voice input isn't available in this browser — try the keyboard instead.");
      return;
    }
    if (micWantedRef.current) {
      micWantedRef.current = false;
      recognitionRef.current.stop();
      setListening(false);
      setAvatarState('idle');
      return;
    }
    try {
      recognitionRef.current.start();
    } catch {
      return;
    }
    micWantedRef.current = true;
    setListening(true);
    setAvatarState('listening');
    setHint(null);
  };

  const toggleKeyboard = () => {
    setKbOpen((v) => !v);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Plays exactly one queued chunk, then chains to the next via
  // source.onended — this is what guarantees back-to-back sentence audio
  // never overlaps even though chunks can arrive (and get decoded) out of
  // step with playback. No-op if something is already playing; the
  // onended handler is what keeps the queue moving on its own.
  const playNextQueuedAudio = () => {
    if (audioPlayingRef.current) return;
    const next = audioQueueRef.current.shift();
    if (!next) return;
    const ctx = playbackCtxRef.current;
    if (!ctx) return;
    audioPlayingRef.current = true;
    const source = ctx.createBufferSource();
    source.buffer = next.buffer;
    source.connect(ctx.destination);
    source.onended = () => {
      audioPlayingRef.current = false;
      playNextQueuedAudio();
    };
    source.start();

    if (avatar3DReadyRef.current) {
      // Lip-sync only — no audio buffer passed, see DigoAvatar3D.tsx.
      avatar3DRef.current?.speak(next.text, next.buffer.duration * 1000);
    }
  };

  // ---- Real voice playback: decode the base64 WAV from an "audio" SSE
  // event and queue it — ALWAYS played here through this component's own
  // AudioContext, regardless of whether the 3D avatar is ready. Originally
  // this handed the buffer to the 3D avatar's own speak() (which plays it
  // through TalkingHead's internal audio pipeline) once that avatar was
  // ready. Live testing showed that path fails silently on turn 2+ (the 3D
  // avatar usually isn't ready yet for turn 1, so turn 1 played fine
  // through this same direct path by accident) — see DigoAvatar3D.tsx's
  // file header note for the root cause. The 3D avatar is now lip-sync-only
  // and never owns audio playback, so voice no longer depends on it at all.
  //
  // 2026-08-19 (later same day): one "audio" SSE event no longer means one
  // full-reply clip — digo_engine.py now streams a separate event per
  // sentence as each finishes synthesizing, arriving in sentence order but
  // NOT necessarily one-per-network-read, so decoding here just enqueues;
  // playNextQueuedAudio above is the only thing that actually starts
  // playback, one chunk at a time. ----
  const enqueueReplyAudio = async (base64: string | undefined, text: string) => {
    if (!base64) {
      console.warn('[Digo] an "audio" SSE event this turn carried no audio_base64 — skipping just that sentence. This points at the backend (TTS synthesis failed for that sentence) rather than the frontend.');
      return;
    }
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = playbackCtxRef.current ?? (playbackCtxRef.current = new AudioCtx());
      const audioBuffer = await ctx.decodeAudioData(bytes.buffer);
      console.info(`[Digo] audio chunk decoded ok: ${audioBuffer.duration.toFixed(2)}s, playbackCtx.state=${ctx.state}, queue depth=${audioQueueRef.current.length}`);
      if (ctx.state === 'suspended') {
        console.warn('[Digo] playbackCtx is suspended — attempting resume() now (browser autoplay policy).');
        await ctx.resume().then(
          () => console.info('[Digo] playbackCtx.resume() succeeded, state=' + ctx.state),
          (e) => console.warn('[Digo] playbackCtx.resume() failed', e)
        );
      }

      audioQueueRef.current.push({ buffer: audioBuffer, text });
      playNextQueuedAudio();
    } catch (err) {
      console.warn('[Digo] failed to decode/queue a reply audio chunk', err);
    }
  };

  // Updates the trailing assistant transcript entry in place while a reply
  // streams in (delta chunks arrive many times per turn), or appends a new
  // one if the previous entry was the user's own message — mirrors how
  // `accumulated` is built up in sendMessage below, just kept in state too
  // so it re-renders instead of only living in the bubble/historyRef.
  const upsertAssistantTranscript = (text: string) => {
    setTranscript((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'assistant') {
        return [...prev.slice(0, -1), { role: 'assistant', text }];
      }
      return [...prev, { role: 'assistant', text }];
    });
  };

  // ---- Real chat: POST /digo/chat, manual SSE parse (same pattern as CoPilotPanel.tsx) ----
  const sendMessage = async (message: string) => {
    const trimmed = message.trim();
    if (!trimmed || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setAvatarState('speaking');
    showBubble('You', trimmed, 0);
    setTranscript((prev) => [...prev, { role: 'user', text: trimmed }]);

    const historyForRequest = historyRef.current;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/digo/chat`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: trimmed, history: historyForRequest }),
      });

      if (!response.ok || !response.body) {
        throw new Error('Failed to reach Digo');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      let repliedYet = false;
      let audioEventSeen = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          try {
            const parsed = JSON.parse(data);
            console.debug('[Digo] SSE event:', parsed.type);
            if (parsed.type === 'delta') {
              accumulated += parsed.content || '';
              repliedYet = true;
              showBubble('Digo', accumulated, 0);
              upsertAssistantTranscript(accumulated);
            } else if (parsed.type === 'done') {
              historyRef.current = [
                ...historyRef.current,
                { role: 'user', content: trimmed },
                { role: 'assistant', content: accumulated },
              ];
              lastReplyTextRef.current = accumulated;
              showBubble('Digo', accumulated);
              upsertAssistantTranscript(accumulated);
            } else if (parsed.type === 'audio') {
              // Each event carries the exact sentence it was synthesized
              // from (parsed.text, added 2026-08-19) — audio chunks now
              // routinely arrive BEFORE "done" (per-sentence streaming, see
              // digo_engine.py), so lastReplyTextRef (only set on "done")
              // is a fallback for older/unexpected payload shapes only.
              audioEventSeen = true;
              await enqueueReplyAudio(parsed.audio_base64, parsed.text || lastReplyTextRef.current);
            } else if (parsed.type === 'error') {
              const msg = parsed.error || 'Something went wrong.';
              showBubble('Digo', msg);
              upsertAssistantTranscript(msg);
            }
          } catch {
            // ignore malformed SSE line, matches CoPilotPanel.tsx's own handling
          }
        }
      }
      if (!repliedYet) {
        const msg = "Didn't get a response — try again.";
        showBubble('Digo', msg);
        upsertAssistantTranscript(msg);
      }
      if (!audioEventSeen) {
        console.warn('[Digo] this turn\'s SSE stream never included an "audio" event at all — synthesis failed or wasn\'t attempted server-side. Check the backend logs for "[Digo] account_id=... TTS synthesis failed" around this request.');
      }
    } catch (err: any) {
      const msg = err?.message || "Couldn't reach Digo — try again in a moment.";
      showBubble('Digo', msg);
      upsertAssistantTranscript(msg);
    } finally {
      busyRef.current = false;
      setBusy(false);
      // Mic never turned off for this turn (continuous listening) — reflect
      // that in the avatar state instead of always dropping to idle, so the
      // listening ring/hint stays accurate.
      setAvatarState(micWantedRef.current ? 'listening' : 'idle');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = inputValue.trim();
    if (!v) return;
    setInputValue('');
    setKbOpen(false);
    sendMessage(v);
  };

  return (
    <div className="digo-launcher-root">
      {!open && (
        <button className="digo-icon-btn" onClick={openDigo} aria-label="Open Digo">
          {imgFailed ? (
            <AvatarFallback />
          ) : (
            <img src={AVATAR_SRC} alt="" onError={() => setImgFailed(true)} />
          )}
        </button>
      )}

      {open && (
        <div className="digo-stage">
          {bubble && (
            <div className={`digo-bubble ${bubbleShow ? 'show' : ''}`}>
              <span className="digo-bubble-role">{bubble.role}</span>
              <span>{bubble.text}</span>
            </div>
          )}

          {/* Photo avatar: the only thing shown until the 3D avatar is ready,
              and the permanent fallback if it fails — but not layered under
              the 3D canvas once that's up, so there's never a photo circle
              showing through its transparent background. */}
          {!avatar3DReady && (
            <div
              className={`digo-avatar-wrap ${popping ? 'popping' : ''} ${settled ? 'settled' : ''}`}
              data-state={avatarState}
            >
              <div className="digo-avatar-shadow" />
              <div className="digo-avatar-ring" />
              <div className="digo-avatar-photo-frame">
                {imgFailed ? (
                  <AvatarFallback />
                ) : (
                  <img src={AVATAR_SRC} alt="Digo" onError={() => setImgFailed(true)} />
                )}
              </div>
            </div>
          )}

          {/* Real 3D lip-synced avatar — no panel behind it (transparent
              renderer clear color, set in DigoAvatar3D.tsx). */}
          {!avatar3DFailed && (
            <div className={`digo-avatar-3d-wrap ${avatar3DReady ? 'visible' : ''}`}>
              <DigoAvatar3D
                ref={avatar3DRef}
                onReady={() => {
                  setAvatar3DReady(true);
                  avatar3DReadyRef.current = true;
                }}
                onError={() => setAvatar3DFailed(true)}
              />
            </div>
          )}

          <button
            type="button"
            className={`digo-orbit-btn digo-mic-btn floaty ${listening ? 'listening' : ''}`}
            onClick={toggleMic}
            aria-label="Ask Digo by voice"
          >
            {listening && <span className="digo-mic-rings" aria-hidden="true" />}
            <span className="digo-mic-icon" aria-hidden="true" />
          </button>

          <button
            type="button"
            className="digo-orbit-btn digo-kb-btn floaty"
            onClick={toggleKeyboard}
            disabled={busy}
            aria-label="Type a question to Digo"
          >
            <span className="digo-kb-icon" aria-hidden="true" />
          </button>

          {kbOpen && (
            <form className="digo-inline-form" onSubmit={handleSubmit}>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask Digo…"
                disabled={busy}
                aria-label="Ask Digo"
              />
              <button type="submit" className="digo-inline-send" disabled={busy || !inputValue.trim()} aria-label="Send">
                →
              </button>
            </form>
          )}

          {hint && <p className="digo-hint">{hint}</p>}

          <button
            type="button"
            className="digo-orbit-btn"
            style={{ position: 'absolute', left: -6, top: -6, width: 24, height: 24, fontSize: 13 }}
            onClick={closeDigo}
            aria-label="Close Digo"
          >
            ×
          </button>
        </div>
      )}

      {/* Full conversation history, always visible while open — a sibling
          of .digo-stage, not layered inside it, so it doesn't interfere with
          the stage's own absolutely-positioned avatar/buttons. Placed below
          rather than to the side: .digo-launcher-root is pinned to the
          screen's right edge (right: 30px), so a right-side panel would run
          off-screen; below has room to grow regardless of viewport width. */}
      {open && transcript.length > 0 && (
        <div className="digo-transcript" aria-label="Digo conversation history">
          {transcript.map((entry, i) => (
            <div key={i} className={`digo-transcript-row digo-transcript-${entry.role}`}>
              <span className="digo-transcript-role">{entry.role === 'user' ? 'You' : 'Digo'}</span>
              <span className="digo-transcript-text">{entry.text}</span>
            </div>
          ))}
          <div ref={transcriptEndRef} />
        </div>
      )}
    </div>
  );
};

export default DigoLauncher;
